/**
 * Types for the complexity-adjustment model (internally "FlowBot").
 *
 * FlowBot replaces random phrase ordering in the Sight Reading Trainer with a
 * per-phrase recurrence that slides a difficulty "band" up when the user is
 * cruising and down when they are struggling, keeping them in a productive flow
 * zone. Source of truth for the maths is `ComplexityOracle.xlsx`; the reverse-
 * engineered semantics are documented in
 * `documentation/complexity-oracle-findings.md`.
 *
 * These types are deliberately free of Phaser and xstate imports so that the
 * pure model can be unit-tested — and validated against the oracle — in
 * isolation from the scene.
 */

/** Per-phrase performance grade.
 *
 *  Derived from the phrase's *complexity* error tally, which is maintained
 *  separately from `EventStream`'s accuracy scoring: a red note contributes
 *  1.0 and a yellow note 0.5, per the engineering spec. (`EventStream`
 *  scores semi-errors at 0.7 for accuracy purposes; that scale is NOT used
 *  here, because the oracle's thresholds were calibrated against 0.5.) */
export enum Verdict {
  /** `error_total <= green_max` — the band moves UP. */
  Green = 'G',
  /** `green_max < error_total <= yellow_max` — the band HOLDS. */
  Yellow = 'Y',
  /** `error_total > yellow_max` — the band moves DOWN. */
  Red = 'R',
}

/**
 * Tunables for one tier. Defaults are the oracle's active preset (column I of
 * `ComplexityOracle.xlsx`).
 *
 * ⚠️ `aim`/`gim` differ from the values quoted in `complexity.md` and
 * `complexity-engineering-spec.md` (which both say `gim: 1.0, aim: 0.5`).
 * Those docs are stale — that pair appears in no column of the oracle and does
 * not reproduce the golden vector. See findings §1, Conflict 1.
 */
export interface TierConfig {
  /** CR_Min seed for assessment phrases 1 and 2. Oracle `C3`. */
  complexityStart: number;
  /** CVR — band width. `CR_Max = min(CR_Min + cvr, 1)`. Also caps `CR_Min` at `1 - cvr`. */
  cvr: number;
  /** CAI — base up-step on green. */
  cai: number;
  /** CDI — base down-step on red. */
  cdi: number;
  /** PAD — application delay, counted in *assessment* phrases only.
   *
   *  A verdict earned at assessment phrase `p` drives the floor at `p + pad`.
   *  This mirrors the render pipeline: `PhraseManager` requests the next phrase
   *  at phrase end and consumes it at measure 2 of the following phrase, so a
   *  phrase requested at the end of `p` first *plays* as `p + 2`. Web = 2,
   *  mobile = 3. */
  pad: number;
  /** AIM — arithmetic (additive) streak multiplier. `1.0` with Streak Boost ON,
   *  `0.0` with it OFF. */
  aim: number;
  /** GIM — geometric (multiplicative) streak multiplier. `2.0` with Streak
   *  Boost ON, `1.0` with it OFF. */
  gim: number;
  /** CAT — chevron eligibility threshold. Not used by the recurrence; carried
   *  here because it is per-tier config. */
  cat: number;
  /** `error_total <= greenMax` classifies as {@link Verdict.Green}. */
  greenMax: number;
  /** `error_total <= yellowMax` classifies as {@link Verdict.Yellow}, else red. */
  yellowMax: number;

  // ---- Flow Regeneration (rest phrases) ----

  /** Assessment phrases that must elapse between injected rest phrases. */
  restCadence: number;
  /** How far BELOW the band floor rest phrases are drawn, in absolute
   *  normalized complexity: `rest = max(crMin - restPhraseDepth, 0)`.
   *
   *  0.20 per product decision 2026-07-18 ("20% below the current band floor").
   *  `complexity-engineering-spec.md` says 0.10; `complexity.md` says 20%. The
   *  latter was chosen and both are user-customisable anyway. */
  restPhraseDepth: number;
  /** Live accuracy (0-100) at or above which rest phrases are never injected —
   *  the user is in flow and must not be interrupted. */
  restAccuracyThreshold: number;
  /** How many consecutive unchanged band floors mean the user is STUCK.
   *
   *  ⚠️ This is NOT the oracle's `PLATEAU`. That variable is an anti-jitter
   *  cooldown meaning "the band just moved" — the opposite of stuck. Because
   *  the cooldown makes a progressing band alternate move/hold, this window
   *  must exceed 2 for a climbing user not to read as stuck. */
  stuckWindow: number;
}

/**
 * Source of the live accuracy percentage that gates rest phrases and chevrons.
 *
 * The spec calls this "WMA", but no weighted moving average exists in the
 * client today — `EventStream.calcAccuracy()` is a rank-weighted recency ratio
 * over the accuracy queue. This interface exists so a true WMA can be
 * substituted later without touching the complexity model.
 *
 * TODO(complexity): replace the EventStream-backed implementation with a real
 * WMA once product confirms the intended definition. See findings §7 Q6.
 */
export interface AccuracySource {
  /** Live accuracy as a percentage in [0, 100]. */
  currentAccuracy(): number;
}

/**
 * The oracle's active preset — column I of `ComplexityOracle.xlsx`, which every
 * formula in the sheet references via `$I$70`–`$I$76`.
 *
 * This is the Streak Boost **ON** preset. See {@link STREAK_BOOST_OFF}.
 */
export const DEFAULT_TIER_CONFIG: Readonly<TierConfig> = Object.freeze({
  complexityStart: 0.2,
  cvr: 0.1,
  cai: 0.1,
  cdi: 0.1,
  pad: 2,
  aim: 1.0,
  gim: 2.0,
  cat: 0.8,
  greenMax: 1.0,
  yellowMax: 3.0,
  restCadence: 4,
  restPhraseDepth: 0.2,
  restAccuracyThreshold: 95,
  stuckWindow: 4,
});

/**
 * Streak Boost OFF — oracle column L.
 *
 * Setting `aim = 0, gim = 1` makes every green step exactly `cai` and every red
 * step exactly `cdi`, which is precisely the "flat Climb/Drop Rate" behaviour
 * `complexity.md` describes for the toggle. Streak Boost therefore needs no
 * special-casing in the recurrence — it is a two-parameter swap.
 */
export const STREAK_BOOST_OFF: Readonly<Pick<TierConfig, 'aim' | 'gim'>> = Object.freeze({
  aim: 0.0,
  gim: 1.0,
});

/**
 * One row of the model's output: the band in effect for a single assessment
 * phrase.
 *
 * `idx` is the 1-based index into the *assessment stream*, NOT the render
 * queue. Rest phrases are interleaved into playback but are not members of the
 * assessment stream and do not consume an index.
 */
export interface AssessmentBand {
  /** 1-based assessment-stream index. */
  idx: number;
  /** Band floor in effect for this phrase. `0 <= crMin <= 1 - cvr`. */
  crMin: number;
  /** Band ceiling: `min(crMin + cvr, 1)`. */
  crMax: number;
  /** The PAD-delayed verdict that drove this row: `verdict[idx - pad]`.
   *  `undefined` for the seed rows, which have no driver. */
  driver?: Verdict;
  /** The oracle's `PLATEAU` value for this row, retained under a truthful name.
   *
   *  ⚠️ Despite the sheet's name this is NOT stuck-detection — it is an
   *  anti-jitter cooldown: `>= 5 - pad` means the band moved on the previous
   *  step, so this step is suppressed. For genuine stuck-detection see
   *  `isStuck()` in `flowRegeneration.ts`. Kept for telemetry and for
   *  reproducing any row against the oracle by hand. */
  cooldown: number;
}
